import type { SensorStatus } from '@atnt/shared';

/**
 * Live status colours (docs/design/02-constructor.md §2.10). `offline` = stale / no data, `unknown` = no binding
 * or nothing received yet — never shown as green (legacy bug).
 */
export interface StatusStyle {
  /** dot / legend colour */
  dot: string;
  /** 10 % halo around viewer dots */
  halo: string;
  /** Badge Small Tinted (tables) */
  badgeBg: string;
  badgeFg: string;
}

export const STATUS_STYLE: Record<SensorStatus, StatusStyle> = {
  normal: { dot: '#03e42d', halo: '#03e42d1a', badgeBg: '#00996633', badgeFg: '#009966' },
  warning: { dot: '#f0c429', halo: '#f0c4291a', badgeBg: '#cfd43633', badgeFg: '#cfd436' },
  danger: { dot: '#e10202', halo: '#e102021a', badgeBg: '#ca3b3d33', badgeFg: '#ca3b3d' },
  offline: { dot: '#99a3b8', halo: '#99a3b81a', badgeBg: '#99a3b833', badgeFg: '#99a3b8' },
  unknown: { dot: '#ffffff66', halo: 'transparent', badgeBg: '#ffffff1a', badgeFg: '#ffffff99' },
};

/** Editor pin palette (429:17364): solid dot + 30 % ring. */
export const STATUS_RING: Record<SensorStatus, string> = {
  normal: '#03e42d4d',
  warning: '#f0c4294d',
  danger: '#e102024d',
  offline: '#99a3b84d',
  unknown: '#ffffff26',
};

const SEVERITY: Record<SensorStatus, number> = { unknown: 0, normal: 1, offline: 2, warning: 3, danger: 4 };

/** A device's status = the worst over all its bound sensors (legacy behaviour, chotqol-legacy §3.5). */
export function worstStatus(statuses: Iterable<SensorStatus>): SensorStatus {
  let worst: SensorStatus = 'unknown';
  for (const s of statuses) if (SEVERITY[s] > SEVERITY[worst]) worst = s;
  return worst;
}

export const statusSeverity = (s: SensorStatus) => SEVERITY[s];

/** i18n key (namespace `constructor`) of the status label: "Normal" / "Ogohlantirish" / "Xavf" / "Ma'lumot yo'q". */
export const statusLabelKey = (s: SensorStatus) => `status.${s}` as const;
